import bizAiService from '@/modules/bizai/services/BizAiService';
import {
  PULSE_VIEW,
  PULSE_CATEGORY,
  PULSE_CATEGORY_ORDER,
  PULSE_PAGE_LIMIT,
  PULSE_STATUS,
} from '@/modules/pulse/utils/pulseConstants';

const emptyCounts = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: 0,
  [PULSE_CATEGORY.UPCOMING]: 0,
  [PULSE_CATEGORY.FYI]: 0,
});

const emptyItems = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: [],
  [PULSE_CATEGORY.UPCOMING]: [],
  [PULSE_CATEGORY.FYI]: [],
});

const emptyPages = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.UPCOMING]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.FYI]: { page: 1, hasMore: false },
});

const state = {
  view: PULSE_VIEW.MY,
  selectedCategory: PULSE_CATEGORY.NEEDS_ATTENTION,
  itemsByCategory: emptyItems(),
  counts: emptyCounts(),
  pageByCategory: emptyPages(),
  loading: false,
  error: null,
  pendingUids: [],
};

const getters = {
  view: (s) => s.view,
  selectedCategory: (s) => s.selectedCategory,
  counts: (s) => s.counts,
  totalActive: (s) => Object.values(s.counts).reduce((sum, count) => sum + count, 0),
  itemsByCategory: (s) => (category) => s.itemsByCategory[category] || [],
  pageByCategory: (s) => (category) => s.pageByCategory[category] || { page: 1, hasMore: false },
  selectedItems: (s) => s.itemsByCategory[s.selectedCategory] || [],
  // Groups the selected category's items by action_group, preserving first-seen order.
  selectedGroups: (s, g) => {
    const groups = [];
    const indexByKey = new Map();
    g.selectedItems.forEach((item) => {
      const key = item.action_group || '';
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({ key, label: item.action_group || null, items: [] });
      }
      groups[indexByKey.get(key)].items.push(item);
    });
    return groups;
  },
  selectedPage: (s) => s.pageByCategory[s.selectedCategory] || { page: 1, hasMore: false },
  isLoading: (s) => s.loading,
  error: (s) => s.error,
  isSelectedEmpty: (s) => (s.itemsByCategory[s.selectedCategory] || []).length === 0,
  isEmpty: (s, g) => g.totalActive === 0,
  isPending: (s) => (uid) => s.pendingUids.includes(uid),
  itemsByContext: (s) => (contextUid) => PULSE_CATEGORY_ORDER
    .flatMap((category) => s.itemsByCategory[category])
    .filter((item) => item?.context?.context_uid === contextUid),
};

const mutations = {
  setView(state, view) {
    state.view = view;
  },
  setSelectedCategory(state, category) {
    state.selectedCategory = category;
  },
  setCategory(state, { category, items, page }) {
    state.itemsByCategory = { ...state.itemsByCategory, [category]: items };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  appendCategory(state, { category, items, page }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: [...state.itemsByCategory[category], ...items],
    };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  setCounts(state, counts) {
    state.counts = {
      [PULSE_CATEGORY.NEEDS_ATTENTION]: counts[PULSE_CATEGORY.NEEDS_ATTENTION] || 0,
      [PULSE_CATEGORY.UPCOMING]: counts[PULSE_CATEGORY.UPCOMING] || 0,
      [PULSE_CATEGORY.FYI]: counts[PULSE_CATEGORY.FYI] || 0,
    };
  },
  removeItem(state, { category, uid }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: state.itemsByCategory[category].filter((item) => item.uid !== uid),
    };
    if (state.counts[category] > 0) {
      state.counts = { ...state.counts, [category]: state.counts[category] - 1 };
    }
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  addPending(state, uid) {
    if (!state.pendingUids.includes(uid)) state.pendingUids = [...state.pendingUids, uid];
  },
  removePending(state, uid) {
    state.pendingUids = state.pendingUids.filter((pendingUid) => pendingUid !== uid);
  },
};

const findCategoryOfItem = (s, uid) => PULSE_CATEGORY_ORDER.find(
  (category) => s.itemsByCategory[category].some((item) => item.uid === uid),
);

const actions = {
  async setView({ commit, dispatch }, view) {
    commit('setView', view);
    await dispatch('fetchFeed');
  },
  // Switching the active category triggers a fresh fetch for that category only.
  async selectCategory({ commit, dispatch, state: s }, category) {
    if (category === s.selectedCategory) return;
    commit('setSelectedCategory', category);
    await dispatch('fetchFeed');
  },
  // Fetches only the currently selected category. Counts for every category are
  // returned alongside any single-category response, so the tabs stay accurate.
  async fetchFeed({ commit, state: s }) {
    const { selectedCategory } = s;
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await bizAiService.getRecommendations({
        category: selectedCategory,
        per_page: PULSE_PAGE_LIMIT,
        include: 'counts',
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      commit('setCategory', {
        category: selectedCategory,
        items,
        page: { page: 1, hasMore: items.length < (counts[selectedCategory] || 0) },
      });
      commit('setCounts', counts);
    } catch (error) {
      commit('setError', error);
      console.error('Failed to fetch Pulse feed', error);
    } finally {
      commit('setLoading', false);
    }
  },
  async loadMore({ commit, state: s }, category) {
    const page = s.pageByCategory[category];
    if (!page?.hasMore) return;
    try {
      const nextPage = (page.page || 1) + 1;
      const response = await bizAiService.getRecommendations({
        category,
        per_page: PULSE_PAGE_LIMIT,
        include: 'counts',
        page: nextPage,
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      const loaded = (s.itemsByCategory[category]?.length || 0) + items.length;
      commit('appendCategory', {
        category,
        items,
        page: { page: nextPage, hasMore: loaded < (counts[category] || loaded) },
      });
      commit('setCounts', counts);
    } catch (error) {
      console.error('Failed to load more Pulse items', error);
    }
  },
  markCompleted({ dispatch }, uid) {
    return dispatch('runAction', { uid, state: PULSE_STATUS.COMPLETED });
  },
  dismissItem({ dispatch }, uid) {
    return dispatch('runAction', { uid, state: PULSE_STATUS.DISMISSED });
  },
  async runAction({ commit, state: s }, { uid, state }) {
    const category = findCategoryOfItem(s, uid);
    commit('addPending', uid);
    try {
      await bizAiService.updateRecommendation(uid, state);
      if (category) commit('removeItem', { category, uid });
    } catch (error) {
      commit('setError', error);
      console.error(`Failed to update Pulse item (${state})`, error);
    } finally {
      commit('removePending', uid);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
